import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, Share2, Sparkles, X } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { burstConfetti } from "./confetti";
import { buildCardFilename, downloadBlob, renderWinCard, shareBlobAsFile } from "@/lib/winCard";
import { linkifyText } from "@/lib/linkify";

const DEFAULT_REDIRECT = "https://citydooh.com";
const AUTO_REDIRECT_MS = 15_000;

/**
 * Post-spin winning popup.
 *
 * Props:
 *   prize            — { label, gift_name, image_url, is_better_luck, terms }
 *   couponCode       — string (nullable for "better luck" outcomes)
 *   campaignName     — string (used in share + subtitle)
 *   vendorBusiness   — string (used in share + subtitle)
 *   campaignUrl      — public play URL to share; falls back to window.location.href
 *   redirectUrl      — post-spin redirect target from admin pricing;
 *                      falls back to DEFAULT_REDIRECT if missing / empty
 *
 * Navigation contract:
 *   • Continue / X (close)  → cancel timer, navigate IMMEDIATELY
 *   • 15-second idle timer  → navigate to redirectUrl
 *   • Copy code / Share      → cancel timer, popup stays open, NO navigate.
 *                              User must then click Continue/X manually.
 *   • "Powered by CityDooh" pill  → opens citydooh.com in a NEW TAB via
 *                              target="_blank". Deliberately has no onClick
 *                              handler, so it does NOT cancel the timer,
 *                              does NOT navigate the current page, and
 *                              does NOT count as an engagement.
 *   • Exactly ONE navigation ever fires — a hasNavigated ref guards races
 *     between the timeout and a click.
 */
export const PrizeRevealModal = ({
  open,
  onOpenChange,
  prize,
  couponCode,
  campaignName,
  vendorBusiness,
  campaignUrl,
  redirectUrl,
  logoUrl,
  thumbnailUrl,
  confettiColors,
  primaryColor,
  // Drives share-caption + acquisition CTA wording so a Scratch Card
  // winner never sees copy about "spinning a wheel".
  gameType = "spin_wheel",
}) => {
  const timerRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Cache the rendered win-card blob so Download + Share re-use it
  // instead of re-drawing the canvas from scratch on every click.
  const cardBlobRef = useRef(null);

  const isBetterLuck = !!prize?.is_better_luck;
  const showCoupon = !!couponCode && !isBetterLuck;

  const prizeLabelForCard = prize?.gift_name || prize?.label || "";
  const logoSrc = logoUrl ? assetUrl(logoUrl) : null;
  const thumbnailSrc = thumbnailUrl ? assetUrl(thumbnailUrl) : null;

  /* Issue #2 — Dynamic prize-name sizing.
     Short prizes ("Free Gift", "5% Off") stay big and celebratory;
     long ones ("Complimentary Family Dinner") scale down so the popup
     never has to wrap awkwardly or push the coupon below the fold.
     We stick to Tailwind size tokens so the whole thing is CSS-only. */
  const prizeDisplayText = String(prizeLabelForCard || "A Prize");
  const prizeLen = prizeDisplayText.length;
  const prizeSizeClass =
    prizeLen <= 10
      ? "text-6xl md:text-7xl"       // "Free Gift", "5% Off"
      : prizeLen <= 18
        ? "text-5xl md:text-6xl"     // "Free Movie Ticket"
        : prizeLen <= 30
          ? "text-4xl md:text-5xl"   // "Complimentary Family Dinner"
          : "text-3xl md:text-4xl";  // very long

  /* Issue #4 helper — coupon text also scales down for long codes so
     it never overflows the yellow ticket pill. */
  const couponLen = String(couponCode || "").length;
  const couponSizeClass =
    couponLen <= 8
      ? "text-4xl md:text-5xl"
      : couponLen <= 14
        ? "text-3xl md:text-4xl"
        : couponLen <= 22
          ? "text-2xl md:text-3xl"
          : "text-xl md:text-2xl";

  const getCardBlob = useCallback(async () => {
    if (cardBlobRef.current) return cardBlobRef.current;
    const { blob } = await renderWinCard({
      prizeLabel: prizeLabelForCard,
      couponCode,
      campaignName,
      vendorBusiness,
      logoUrl: logoSrc,
      thumbnailUrl: thumbnailSrc,
      primaryColor: primaryColor || "#F5A800",
    });
    cardBlobRef.current = blob;
    return blob;
  }, [prizeLabelForCard, couponCode, campaignName, vendorBusiness, logoSrc, thumbnailSrc, primaryColor]);

  const effectiveRedirect =
    typeof redirectUrl === "string" && redirectUrl.trim().length > 0
      ? redirectUrl.trim()
      : DEFAULT_REDIRECT;

  const cancelTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const navigateOnce = useCallback(() => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    cancelTimer();
    try {
      window.location.assign(effectiveRedirect);
    } catch {
      // Last-ditch fallback if the URL is somehow malformed
      window.location.href = DEFAULT_REDIRECT;
    }
  }, [effectiveRedirect, cancelTimer]);

  // Confetti + 10-second auto-redirect timer.
  useEffect(() => {
    if (!open) {
      cancelTimer();
      return;
    }
    hasNavigatedRef.current = false;
    setCopiedCoupon(false);
    setCopiedShare(false);
    // A new prize means the previously cached card image is stale.
    cardBlobRef.current = null;
    if (!isBetterLuck) {
      const t = setTimeout(() => burstConfetti(confettiColors), 120);
      timerRef.current = setTimeout(() => {
        navigateOnce();
      }, AUTO_REDIRECT_MS);
      return () => {
        clearTimeout(t);
        cancelTimer();
      };
    }
    // "Better luck" outcome — no auto-redirect (no prize to leave), close
    // is the only exit.
    return () => cancelTimer();
  }, [open, isBetterLuck, confettiColors, cancelTimer, navigateOnce]);

  const handleContinue = () => {
    cancelTimer();
    onOpenChange(false);
    // Only auto-navigate for winning outcomes; "better luck" just closes.
    if (!isBetterLuck) navigateOnce();
  };

  const handleClose = () => {
    handleContinue();
  };

  const handleCopyCoupon = async () => {
    if (!couponCode) return;
    // User is engaging — do NOT navigate.
    cancelTimer();
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopiedCoupon(true);
      toast.success("Copied!");
      setTimeout(() => setCopiedCoupon(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy the code manually.");
    }
  };

  const handleDownload = async () => {
    if (!showCoupon) return;
    // User is engaging with the win — freeze the auto-redirect timer.
    cancelTimer();
    setDownloading(true);
    try {
      const blob = await getCardBlob();
      if (!blob) {
        toast.error("Couldn't build the image — try again.");
        return;
      }
      const safeCode = String(couponCode || "citydooh-win").replace(/[^\w-]+/g, "_");
      downloadBlob(blob, buildCardFilename({ campaignName, couponCode: safeCode }));
      toast.success("Saved to your device");
    } catch {
      toast.error("Couldn't download the image.");
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    cancelTimer();
    const prizeLabel = prize?.gift_name || prize?.label || "a prize";
    const biz = vendorBusiness || "a local store";
    const url =
      (typeof campaignUrl === "string" && campaignUrl.trim()) ||
      (typeof window !== "undefined" ? window.location.href : "");
    // Share-caption copy branches so a Scratch Card win reads "scratching
    // ...'s card" rather than the wheel-specific "spinning ...'s wheel".
    const isScratchShare = gameType === "scratch_card";
    const shareAction = isScratchShare ? "scratching" : "spinning";
    const shareNoun = isScratchShare ? "scratch card" : "wheel";
    const text = `I just won ${prizeLabel} ${shareAction} ${biz}'s ${shareNoun}! Try your luck: ${url}`;

    // Preferred path: attach the win-card PNG so the receiving app
    // (WhatsApp / IG DM / etc.) shares the image + the caption. Only
    // available where `navigator.canShare({ files })` returns true —
    // most modern mobile browsers, but never desktop Firefox.
    if (showCoupon) {
      try {
        const blob = await getCardBlob();
        if (blob) {
          const safeCode = String(couponCode || "citydooh-win").replace(/[^\w-]+/g, "_");
          const result = await shareBlobAsFile(
            blob,
            buildCardFilename({ campaignName, couponCode: safeCode }),
            { text, url },
          );
          if (result === "shared") return;
          if (result === "cancelled") return; // user closed the sheet
          // Anything else — fall through to text-only share below.
        }
      } catch {
        // fall through
      }
    }

    // Text-only share (link + caption). Same as before the file-share
    // upgrade — kept as the ultimate fallback for browsers without any
    // Web Share support at all.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ text, url });
        return;
      } catch (err) {
        if (err && (err.name === "AbortError" || err.code === 20)) return;
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedShare(true);
      toast.success("Link copied — paste it anywhere!");
      setTimeout(() => setCopiedShare(false), 2200);
    } catch {
      toast.error("Couldn't copy the share link — try again.");
    }
  };

  const rawTerms = (prize?.terms || "").trim();
  const termsNodes = useMemo(() => linkifyText(rawTerms), [rawTerms]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          // Radix invokes this on X, overlay click, and Esc — treat all
          // three as "close = navigate" per the spec.
          handleClose();
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-2 border-black"
        style={{ backgroundColor: "#F7F4EC" }}
        data-testid="prize-reveal-modal"
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.32, ease: "easeOut" }}
              className="p-6 md:p-8"
            >
              <div className="flex justify-end">
                <button
                  onClick={handleClose}
                  className="text-black/60 hover:text-black transition-colors"
                  aria-label="Close"
                  data-testid="prize-reveal-close-button"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="text-center">
                {isBetterLuck ? (
                  <>
                    <Sparkles className="mx-auto h-10 w-10 text-black/50" />
                    <h2
                      className="text-3xl md:text-4xl font-black text-black mt-3 uppercase tracking-tight"
                      style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                      data-testid="prize-reveal-title"
                    >
                      Better luck next time!
                    </h2>
                    <p className="text-black/60 mt-2 font-medium">
                      {prize?.label || "Try again soon"}
                    </p>
                  </>
                ) : (
                  <>
                    {/* "YOU WON" eyebrow — subtle, sits above the big prize.
                        Matches the play-page eyebrow style from Phase 1. */}
                    <p className="uppercase tracking-[0.22em] text-[11px] font-bold text-black/70">
                      You won
                    </p>

                    {/* Prize name — dynamic Anton size based on length
                        (Issue #2). Short prizes stay big and celebratory,
                        long ones scale down so they always fit on-screen
                        without wrapping awkwardly. */}
                    <h2
                      className={`${prizeSizeClass} font-black text-black mt-2 uppercase leading-[0.95] tracking-tight px-2`}
                      style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                      data-testid="prize-reveal-title"
                    >
                      {prizeDisplayText}
                    </h2>

                    {prize?.image_url && (
                      <div className="mx-auto mt-5 h-40 w-40 rounded-2xl overflow-hidden border-2 border-black shadow-[6px_6px_0_#F5A800] bg-white">
                        <img
                          src={assetUrl(prize.image_url)}
                          alt={prize.gift_name || prize.label}
                          className="w-full h-full object-cover"
                          data-testid="prize-reveal-image"
                        />
                      </div>
                    )}

                    {/* Coupon "prize ticket" — yellow pill with thick black
                        border and chunky black offset shadow, echoing the
                        downloadable win-card's ticket treatment (Issue #4).
                        Coupon code uses Anton so it reads as a headline,
                        not a form input. */}
                    {showCoupon && (
                      <div className="mt-6">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/60">
                          Your coupon code
                        </p>
                        <div
                          className="mt-2.5 rounded-full border-2 border-black py-3.5 px-4 shadow-[6px_6px_0_#0A0A0A]"
                          style={{ backgroundColor: "#F5A800" }}
                          data-testid="prize-reveal-coupon-code"
                        >
                          <div
                            className={`${couponSizeClass} text-black tracking-[0.08em] leading-none uppercase`}
                            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                          >
                            {couponCode}
                          </div>
                        </div>
                      </div>
                    )}

                    {rawTerms && (
                      <div
                        className="mt-5 rounded-xl border-2 border-black/15 bg-white p-3 text-left"
                        data-testid="prize-reveal-terms"
                      >
                        <p className="text-[10px] uppercase tracking-widest text-black/60 font-bold">
                          Terms
                        </p>
                        <p className="text-xs text-black/70 mt-1 leading-relaxed whitespace-pre-wrap break-words">
                          {termsNodes.length ? termsNodes : rawTerms}
                        </p>
                      </div>
                    )}

                    {/* Vendor identity — upgraded chip with logo + business
                        + campaign name (Issue #3). Clearly legible, not a
                        buried grey afterthought. */}
                    {(vendorBusiness || campaignName) && (
                      <div
                        className="mt-5 inline-flex items-center gap-3 rounded-full border-2 border-black/15 bg-white/70 backdrop-blur px-4 py-2 max-w-full"
                        data-testid="prize-reveal-vendor-line"
                      >
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt=""
                            className="h-7 w-7 rounded-full object-cover ring-1 ring-black/20"
                          />
                        ) : (
                          <span
                            className="h-7 w-7 rounded-full grid place-items-center text-white text-xs font-black"
                            style={{ backgroundColor: "#0A0A0A", fontFamily: '"Anton", sans-serif' }}
                            aria-hidden="true"
                          >
                            {String(vendorBusiness || campaignName || "C").trim().charAt(0).toUpperCase() || "C"}
                          </span>
                        )}
                        <div className="text-left min-w-0">
                          {vendorBusiness && (
                            <div className="text-sm font-bold text-black leading-tight truncate">
                              {vendorBusiness}
                            </div>
                          )}
                          {campaignName && (
                            <div className="text-[11px] text-black/60 leading-tight truncate">
                              {campaignName}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {isBetterLuck ? (
                  <Button
                    onClick={handleContinue}
                    className="mt-7 h-14 w-full rounded-full uppercase tracking-wider text-black hover:-translate-y-0.5 transition-transform"
                    style={{
                      backgroundColor: "#F5A800",
                      color: "#0A0A0A",
                      boxShadow: "6px 6px 0 #0A0A0A",
                      fontFamily: '"Anton", "Impact", sans-serif',
                      fontSize: 20,
                      letterSpacing: "0.08em",
                    }}
                    data-testid="prize-reveal-done-button"
                  >
                    Close
                  </Button>
                ) : (
                  <div className="mt-7 space-y-3">
                    {/* Primary CTA — CityDooh gold, matches the SPIN NOW
                        pill on the play page (Issue #1 + #5). Chunky black
                        offset shadow ties it to the whole redesigned
                        brand chrome. */}
                    <Button
                      onClick={handleContinue}
                      className="h-14 w-full rounded-full uppercase tracking-wider hover:-translate-y-0.5 transition-transform"
                      style={{
                        backgroundColor: "#F5A800",
                        color: "#0A0A0A",
                        boxShadow: "6px 6px 0 #0A0A0A",
                        fontFamily: '"Anton", "Impact", sans-serif',
                        fontSize: 20,
                        letterSpacing: "0.08em",
                      }}
                      data-testid="prize-reveal-continue-button"
                    >
                      Continue
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      {showCoupon && (
                        <Button
                          variant="outline"
                          onClick={handleCopyCoupon}
                          className="h-11 rounded-full font-semibold border-2 border-black text-black hover:bg-black hover:text-[#F5A800] bg-transparent"
                          data-testid="prize-reveal-copy-button"
                        >
                          <Copy className="h-4 w-4 mr-1.5" />
                          {copiedCoupon ? "Copied!" : "Copy Coupon"}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={handleShare}
                        className={`h-11 rounded-full font-semibold border-2 border-black text-black hover:bg-black hover:text-[#F5A800] bg-transparent ${
                          showCoupon ? "" : "col-span-2"
                        }`}
                        data-testid="prize-reveal-share-button"
                      >
                        <Share2 className="h-4 w-4 mr-1.5" />
                        {copiedShare ? "Link copied!" : "Share with Friends"}
                      </Button>
                    </div>
                    {showCoupon && (
                      <Button
                        variant="outline"
                        onClick={handleDownload}
                        disabled={downloading}
                        className="h-11 w-full rounded-full font-semibold border-2 border-black text-black hover:bg-black hover:text-[#F5A800] bg-transparent disabled:opacity-60"
                        data-testid="prize-reveal-download-button"
                      >
                        <Download className="h-4 w-4 mr-1.5" />
                        {downloading ? "Preparing…" : "Download Image"}
                      </Button>
                    )}
                    <p className="text-[11px] text-black/60 mt-2 leading-relaxed">
                      This page redirects in 15 seconds — tap Copy, Share, or
                      Download to stay a little longer.
                    </p>

                    {/* B4 — "Powered by CityDooh" + gold pill CTA. UNCHANGED
                        from the previous version — plain <a target="_blank">
                        with no onClick, so it does NOT cancel the timer, does
                        NOT navigate the current tab, and does NOT count as
                        engagement. Contract preserved byte-for-byte. */}
                    <div
                      className="mt-4 flex flex-col items-center gap-2"
                      data-testid="prize-reveal-powered-by"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] text-black/60">
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: "#F5A800" }}
                          aria-hidden="true"
                        />
                        <span>Powered by CityDooh</span>
                      </div>
                      <a
                        href="https://citydooh.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-black transition-transform hover:scale-[1.03] focus-visible:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#F5A800]"
                        style={{ backgroundColor: "#F5A800" }}
                        data-testid="prize-reveal-citydooh-cta"
                      >
                        Want an experience like this for your shop? It&apos;s free
                        <span aria-hidden="true">→</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default PrizeRevealModal;
